/**
 * Auth interceptor — works across origins (e.g. roleiq.pro frontend → emergent.host backend)
 * by using Authorization: Bearer headers + localStorage tokens instead of cookies.
 *
 * - REQUEST: attach Authorization header from localStorage to every API call.
 * - RESPONSE: on 401, hit /api/auth/refresh with the stored refresh_token, save the new
 *   access_token, and silently retry the original request.
 * - Cookies are kept as a fallback (withCredentials still true).
 */
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;
const ACCESS_KEY = "roleiq_access_token";
const REFRESH_KEY = "roleiq_refresh_token";

export function setTokens(access, refresh) {
  if (access) localStorage.setItem(ACCESS_KEY, access);
  if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
}

export function clearTokens() {
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
}

export function getAccessToken() {
  return localStorage.getItem(ACCESS_KEY);
}

export function getRefreshToken() {
  return localStorage.getItem(REFRESH_KEY);
}

let isRefreshing = false;
let pendingQueue = [];

function resolveQueue(err, newAccess) {
  pendingQueue.forEach((cb) => cb(err, newAccess));
  pendingQueue = [];
}

let installed = false;

export function installAuthInterceptor() {
  if (installed) return;
  installed = true;

  // 1) REQUEST interceptor — attach bearer token + always send cookies as fallback
  axios.interceptors.request.use((config) => {
    config.withCredentials = true;
    const url = config.url || "";
    // Only attach to our API
    if (API && url.startsWith(API)) {
      const access = getAccessToken();
      if (access && !config.headers?.Authorization) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${access}`;
      }
    }
    return config;
  });

  // 2) RESPONSE interceptor — on 401, refresh + retry once
  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const original = error.config;
      const status = error.response?.status;

      if (!original || status !== 401 || original._retried) {
        return Promise.reject(error);
      }

      const url = original.url || "";
      if (
        url.includes("/api/auth/refresh") ||
        url.includes("/api/auth/login") ||
        url.includes("/api/auth/register") ||
        url.includes("/api/auth/me")
      ) {
        return Promise.reject(error);
      }

      original._retried = true;

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          pendingQueue.push((err, newAccess) => {
            if (err) return reject(err);
            if (newAccess) {
              original.headers = original.headers || {};
              original.headers.Authorization = `Bearer ${newAccess}`;
            }
            resolve(axios(original));
          });
        });
      }

      isRefreshing = true;
      try {
        const refresh = getRefreshToken();
        const body = refresh ? { refresh_token: refresh } : {};
        const r = await axios.post(`${API}/api/auth/refresh`, body, { withCredentials: true });
        const newAccess = r.data?.access_token;
        const newRefresh = r.data?.refresh_token;
        if (newAccess) {
          localStorage.setItem(ACCESS_KEY, newAccess);
          original.headers = original.headers || {};
          original.headers.Authorization = `Bearer ${newAccess}`;
        }
        // Save rotated refresh token (rotation + reuse detection)
        if (newRefresh) {
          localStorage.setItem(REFRESH_KEY, newRefresh);
        }
        isRefreshing = false;
        resolveQueue(null, newAccess);
        return axios(original);
      } catch (refreshErr) {
        isRefreshing = false;
        clearTokens();
        resolveQueue(refreshErr, null);
        return Promise.reject(error);
      }
    }
  );
}
